/**
 * The alert rules of one site — the list both apps' Alert Config shows.
 *
 *   [search]  [All · Critical · Warning …]  [All · On · Off]      7 of 10 rules on  ⟳  + New rule
 *   ON  RULE (name · description · older-rule notes)  SEVERITY  MIN. CONFIDENCE  REPEAT AFTER  ACTIONS
 *
 * Filtering is the list's own; the host owns the rules and the calls (each app has its own API).
 * A row's left edge is its severity; a rule that is off is dimmed. Clicking a row edits it.
 */
import { useMemo, useState } from 'react';
import { useFillHeight } from '../layout/FillHeightCard';
import {
  ArrowPathIcon, Cog6ToothIcon, DocumentDuplicateIcon, ExclamationTriangleIcon, LinkIcon,
  MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import { SeverityTag } from '../alerts/AlertTags';
import { AASwitch } from '../alertActions/AlertActionsControls';
import { conditionsSummary } from './ruleConditionsModel';
import { BTN_PRIMARY, SEARCH_INPUT } from '../common/ui';

/** How far an older rule reaches (a camera list, one camera, a group) — or every camera. */
function scopeOf(r: any): 'cameras' | 'camera' | 'group' | 'all' {
  if (Array.isArray(r.camera_ids)) return 'cameras';
  if (r.camera_id) return 'camera';
  if (r.group_id) return 'group';
  return 'all';
}

export interface AlertRulesListProps {
  rules: any[];
  loading: boolean;
  canConfig: boolean;
  /** Rules whose on/off call is in flight. */
  togglingIds: string[];
  cameras: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  /** How an event type reads in this app. */
  eventTypeLabel: (t: string) => string;
  onToggle: (rule: any) => void;
  onEdit: (rule: any) => void;
  onDuplicate: (id: string) => void;
  onDelete: (rule: any) => void;
  onCreate: () => void;
  onRefresh: () => void;
  error?: string;
  onDismissError?: () => void;
}

type SevFilter = 'all' | 'critical' | 'warning' | 'normal' | '*';
type StateFilter = 'all' | 'on' | 'off';

const SEV_EDGE: Record<string, string> = {
  critical: 'border-l-red-500', warning: 'border-l-amber-500', normal: 'border-l-blue-500', '*': 'border-l-border',
};
const SEV_BAR: Record<string, string> = {
  critical: 'bg-red-500', warning: 'bg-amber-500', normal: 'bg-blue-500', '*': 'bg-brand',
};
const SEV_FILTERS: { value: SevFilter; label: string; dot?: string }[] = [
  { value: 'critical', label: 'Critical', dot: 'bg-red-500' },
  { value: 'warning', label: 'Warning', dot: 'bg-amber-500' },
  { value: 'normal', label: 'Normal', dot: 'bg-blue-500' },
  { value: '*', label: 'Auto', dot: 'bg-muted-foreground' },
];
const sevOf = (r: any) => (['critical', 'warning', 'normal'].includes(r.severity) ? r.severity : '*');

export function ruleTitle(rule: any, eventTypeLabel: (t: string) => string): string {
  if (!rule) return '';
  return rule.name || `Rule: ${eventTypeLabel(rule.event_type)}`;
}

/** "Every time", "30 s", "5 min", "1 h" — the rule's "Don't repeat it for". */
export function repeatText(seconds: unknown) {
  const s = Number(seconds) || 0;
  if (!s) return 'Every time';
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 360) / 10} h`;
}

/** A rule saved when rules picked their cameras still raises alerts only there ('' = none). */
function oldCameraLimit(rule: any, cameras: { id: string; name: string }[], groups: { id: string; name: string }[]): string {
  const cam = (id: string) => cameras.find((c) => c.id === id)?.name || id;
  const scope = scopeOf(rule);
  const listed: string[] = scope === 'cameras' ? rule.camera_ids || [] : [];
  if (scope === 'camera') return cam(rule.camera_id);
  if (scope === 'cameras') return !listed.length ? '' : listed.length <= 2 ? listed.map(cam).join(', ') : `${listed.length} cameras`;
  if (scope === 'group') return `cameras in ${groups.find((g) => g.id === rule.group_id)?.name || rule.group_id}`;
  return '';
}

/** A one-row segmented filter: [All 10][● Critical 6][● Warning 3]. */
function Seg<T extends string>({ value, onChange, options }: {
  value: T; onChange: (v: T) => void; options: { value: T; label: string; count: number; dot?: string }[];
}) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-xl border border-border bg-card">
      {options.map((o, i) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} aria-pressed={on}
            className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-[13px] transition-colors ${i ? 'border-l border-border' : ''} ${
              on ? 'bg-foreground font-medium text-background' : 'text-foreground hover:bg-muted'}`}>
            {o.dot && <span className={`h-2 w-2 rounded-full ${o.dot}`} />}
            {o.label}
            <span className={`tabular-nums ${on ? 'opacity-80' : 'text-muted-foreground'}`}>{o.count}</span>
          </button>
        );
      })}
    </div>
  );
}

const TH = 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';
const iconBtn = 'rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';

export default function AlertRulesList({
  rules, loading, canConfig, togglingIds, cameras, groups, eventTypeLabel,
  onToggle, onEdit, onDuplicate, onDelete, onCreate, onRefresh, error, onDismissError,
}: AlertRulesListProps) {
  const [search, setSearch] = useState('');
  const [sev, setSev] = useState<SevFilter>('all');
  const [state, setState] = useState<StateFilter>('all');

  const onCount = rules.filter((r) => r.is_active).length;
  const sevCount = (v: SevFilter) => rules.filter((r) => sevOf(r) === v).length;
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter((r) => {
      if (sev !== 'all' && sevOf(r) !== sev) return false;
      if (state === 'on' && !r.is_active) return false;
      if (state === 'off' && r.is_active) return false;
      if (!q) return true;
      return [r.name, r.description, eventTypeLabel(r.event_type), r.event_type, r.sub_event_type, r.severity]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rules, search, sev, state, eventTypeLabel]);
  const filtering = !!search.trim() || sev !== 'all' || state !== 'all';
  const clear = () => { setSearch(''); setSev('all'); setState('all'); };
  const fill = useFillHeight();

  return (
    // One white card on the page background: the search and filters on top, then the table —
    // a tinted header row and one row per rule, its severity down the left edge. It fills the rest
    // of the screen; more rules than fit scroll under the header.
    <div ref={fill.ref} style={fill.height ? { height: fill.height } : undefined}
      className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
        <label className="relative min-w-[12rem] flex-1 sm:max-w-xs">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search rules, event types…"
            className={SEARCH_INPUT} />
        </label>
        <Seg<SevFilter> value={sev} onChange={setSev} options={[
          { value: 'all', label: 'All', count: rules.length },
          ...SEV_FILTERS.filter((f) => sevCount(f.value) > 0).map((f) => ({ ...f, count: sevCount(f.value) })),
        ]} />
        <Seg<StateFilter> value={state} onChange={setState} options={[
          { value: 'all', label: 'All', count: rules.length },
          { value: 'on', label: 'On', count: onCount },
          { value: 'off', label: 'Off', count: rules.length - onCount },
        ]} />
        <span className="flex-1" />
        <span className="whitespace-nowrap text-[13px] text-muted-foreground">
          <b className="font-semibold text-foreground">{onCount}</b> of {rules.length} rules on
        </span>
        <button type="button" onClick={onRefresh} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Reload rules" title="Reload rules">
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {canConfig && (
          <button type="button" onClick={onCreate} className={BTN_PRIMARY}>
            <PlusIcon className="h-4 w-4" /> New rule
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 border-b border-border bg-red-500/10 px-4 py-2 text-[13px] text-red-600">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{error}</span>
          {onDismissError && <button type="button" onClick={onDismissError} className="text-[12px] underline opacity-80 hover:opacity-100">Dismiss</button>}
        </div>
      )}

      {/* ── The rules ── */}
      <div className="min-h-0 flex-1 overflow-auto">
        {loading && !rules.length ? (
          <div className="space-y-2 p-4">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}</div>
        ) : shown.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
            <Cog6ToothIcon className="h-8 w-8 text-muted-foreground opacity-40" />
            <p className="text-sm font-medium text-foreground">{filtering ? 'No rules match these filters' : 'No alert rules yet'}</p>
            <p className="text-[13px] text-muted-foreground">
              {filtering ? 'Clear the search or filters to see the rest.' : 'Until a rule matches it, an AI detection is recorded but nobody is told.'}
            </p>
            {filtering
              ? <button type="button" onClick={clear} className="mt-1 text-sm text-brand">Clear filters</button>
              : canConfig && <button type="button" onClick={onCreate} className="mt-1 text-sm text-brand">Create your first rule</button>}
          </div>
        ) : (
          <table className="w-full min-w-[60rem] table-fixed text-[13px]">
            <colgroup>
              <col className="w-[6.5rem]" />
              <col />
              <col className="w-[9.5rem]" />
              <col className="w-[11rem]" />
              <col className="w-[8.5rem]" />
              <col className="w-[8rem]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-background">
              <tr className="border-b border-border">
                <th className={`${TH} pl-5`}>On</th>
                <th className={TH}>Rule</th>
                <th className={TH}>Severity</th>
                <th className={TH}>Min. confidence</th>
                <th className={TH}>Repeat after</th>
                <th className={`${TH} pr-5 text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const s = sevOf(r);
                const pct = Math.round((Number(r.min_confidence) || 0) * 100);
                const limit = oldCameraLimit(r, cameras, groups);
                const when = conditionsSummary(r);
                const title = ruleTitle(r, eventTypeLabel);
                return (
                  <tr key={r.id} onClick={canConfig ? () => onEdit(r) : undefined}
                    className={`border-b border-border align-middle transition-colors last:border-0 hover:bg-muted ${canConfig ? 'cursor-pointer' : ''}`}>
                    <td className={`border-l-[3px] ${SEV_EDGE[s]} py-4 pl-5 pr-2`} onClick={(e) => e.stopPropagation()}>
                      <AASwitch checked={!!r.is_active} busy={togglingIds.includes(r.id)} disabled={!canConfig}
                        label={`${r.is_active ? 'Turn off' : 'Turn on'} ${title}`} onChange={() => onToggle(r)} />
                    </td>
                    <td className={`min-w-0 px-4 py-4 ${r.is_active ? '' : 'opacity-60'}`}>
                      <p className="truncate text-[15px] font-semibold text-foreground" title={title}>{title}</p>
                      {r.description && <p className="mt-0.5 truncate text-[13px] text-muted-foreground" title={r.description}>{r.description}</p>}
                      {when && <p className="mt-0.5 truncate text-[12px] text-muted-foreground" title={when}>when {when}</p>}
                      {(limit || r.webhook_enabled) && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {limit && (
                            <span className="rounded-md border border-amber-500 bg-amber-500/10 px-2 py-0.5 text-[12px] font-medium text-amber-700" title="An older rule limited to some cameras — open it to cover every camera">
                              Only {limit}
                            </span>
                          )}
                          {r.webhook_enabled && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[12px] text-muted-foreground" title={r.webhook_url || 'Webhook'}>
                              <LinkIcon className="h-3 w-3" /> Webhook
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className={`px-4 py-4 ${r.is_active ? '' : 'opacity-60'}`}>
                      {s === '*'
                        ? <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Auto</span>
                        : <SeverityTag severity={r.severity} variant="soft" dot className="uppercase tracking-wide" />}
                    </td>
                    <td className={`px-4 py-4 ${r.is_active ? '' : 'opacity-60'}`}>
                      {pct > 0 ? (
                        <span className="flex items-center gap-3">
                          <span className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                            <span className={`block h-full rounded-full ${SEV_BAR[s]}`} style={{ width: `${pct}%` }} />
                          </span>
                          <span className="font-semibold tabular-nums text-foreground">{pct}%</span>
                        </span>
                      ) : <span className="text-[12px] text-muted-foreground">Not checked</span>}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-4 text-[14px] text-foreground ${r.is_active ? '' : 'opacity-60'}`}>{repeatText(r.cooldown_seconds)}</td>
                    <td className="px-4 py-4 pr-5" onClick={(e) => e.stopPropagation()}>
                      {canConfig ? (
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => onEdit(r)} className={iconBtn} aria-label="Edit rule" title="Edit"><PencilSquareIcon className="h-[18px] w-[18px]" /></button>
                          <button type="button" onClick={() => onDuplicate(r.id)} className={iconBtn} aria-label="Duplicate rule" title="Duplicate"><DocumentDuplicateIcon className="h-[18px] w-[18px]" /></button>
                          <button type="button" onClick={() => onDelete(r)} className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-600" aria-label="Delete rule" title="Delete"><TrashIcon className="h-[18px] w-[18px]" /></button>
                        </div>
                      ) : <div className="text-right text-[12px] text-muted-foreground">—</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
